import type { PurDraftCreateInput } from "./purDraftCreatePilot";
import type { PurOrderLine, PurRecord } from "./purApi";
import type { PurReceiptInput } from "./purReceiptCreate";
import { readId } from "./purRead";

// Data rules shared by local review and the write adapters. No permission,
// actor, marker or API-context checks belong in this layer.
export class PurPayloadValidationError extends Error {
  constructor(public messageId: string, public line?: number) {
    super(messageId); Object.setPrototypeOf(this, PurPayloadValidationError.prototype);
  }
}
const block = (messageId = "MEERP_RECEIPT_DATA_ERROR"): never => { throw new PurPayloadValidationError(messageId); };
const withPayloadLine = <T>(line: number, build: () => T): T => {
  try { return build(); }
  catch (error) {
    if (error instanceof PurPayloadValidationError) error.line = line;
    throw error;
  }
};
export const normalizePurPayloadDecimal = (
  value: string | null | undefined, places = 2, positive = false, maxDigits = 12,
  messageId = "MEERP_CREATE_VALIDATION_ERROR",
): string => {
  const match = /^(\d+)(?:\.(\d+))?$/.exec(value?.trim() || "");
  if (!match || (match[2] || "").slice(places).replace(/0/g, "")) return block(messageId);
  const integer = match[1].replace(/^0+(?=\d)/, "");
  const fraction = (match[2] || "").slice(0, places).padEnd(places, "0");
  if (integer.length > maxDigits - places || (positive && !/[1-9]/.test(integer + fraction))) return block(messageId);
  return `${integer}.${fraction}`;
};
const receiptDecimal = (value: string | null, places = 2, positive = false): string =>
  normalizePurPayloadDecimal(value, places, positive, 12, "MEERP_RECEIPT_DATA_ERROR");
const receiptUnits = (value: string | null, places = 2): number => Number(receiptDecimal(value, places).replace(".", ""));
// Python Decimal round(..., 2) uses half-even, not Math.round's half-up.
// Keep the division exact even when cost cents * 10000 exceeds Number precision.
export const receiptPayloadBaseCostCents = (cost: string, factor: string): number => {
  const numerator = BigInt(receiptDecimal(cost).replace(".", "")) * BigInt(10000);
  const denominator = BigInt(receiptDecimal(factor, 4, true).replace(".", ""));
  const quotient = numerator / denominator, remainder = numerator % denominator;
  const comparison = remainder * BigInt(2);
  const rounded = quotient + (comparison > denominator || (comparison === denominator && quotient % BigInt(2) !== BigInt(0)) ? BigInt(1) : BigInt(0));
  const cents = Number(rounded);
  if (!Number.isSafeInteger(cents)) return block("MEERP_DRAFT_UNIT_COST_ERROR");
  return cents;
};
const validDate = (text: string): boolean => {
  const date = new Date(`${text}T00:00:00Z`);
  return /^\d{4}-\d{2}-\d{2}$/.test(text) && Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === text;
};
export const receiptPayloadOrderIssue = (po: PurRecord): string | null => {
  if (!["APPROVED", "PARTIALLY_RECEIVED", "TO_RECEIVE"].includes(po.status)
    || !readId(po.id) || !readId(po.projectId) || !readId(po.vendorId) || !po.items.length) return "MEERP_RECEIPT_SOURCE_ERROR";
  try {
    const seen = new Set<number>(); let remaining = false;
    for (const line of po.items) {
      if (line.kind !== "purchaseOrders" || !readId(line.id) || !readId(line.productId) || !readId(line.unitId)
        || (line.poId !== null && line.poId !== po.id) || seen.has(line.id!)) return "MEERP_RECEIPT_SOURCE_ERROR";
      seen.add(line.id!);
      const ordered = receiptUnits(line.orderedQuantity), received = receiptUnits(line.receivedQuantity);
      receiptDecimal(line.conversionFactor, 4, true); receiptDecimal(line.unitCost);
      if (received > ordered) return "MEERP_RECEIPT_SOURCE_ERROR";
      remaining = remaining || ordered > received;
    }
    return remaining ? null : "MEERP_RECEIPT_NO_REMAINING";
  } catch { return "MEERP_RECEIPT_SOURCE_ERROR"; }
};

const payloadId = (value: unknown): number => {
  const id = readId(value);
  return id === null ? block("MEERP_DRAFT_HEADER_ERROR") : id;
};
export const buildPurDraftCreateData = (input: PurDraftCreateInput): Record<string, unknown> => {
  const isOrder = input.kind === "purchaseOrders";
  if (!["materialRequests", "purchaseOrders"].includes(input.kind) || !input.lines.length || input.lines.length > 100) return block("MEERP_CREATE_VALIDATION_ERROR");
  const project = payloadId(input.projectId), storage = payloadId(input.storageId);
  const vendor = isOrder ? payloadId(input.vendorId) : null;
  const headerVat = isOrder ? normalizePurPayloadDecimal(input.vatRate, 2, false, 5) : null;
  if (headerVat !== null && Number(headerVat) > 100) return block("MEERP_CREATE_VALIDATION_ERROR");
  const seen = new Set<number>();
  const items = input.lines.map((line, index) => withPayloadLine(index + 1, () => {
    const product = payloadId(line.productId), unit = payloadId(line.unitId);
    if (seen.has(product)) return block("MEERP_DRAFT_DUPLICATE_ERROR");
    seen.add(product);
    const common = { product, unit, conversion_factor: normalizePurPayloadDecimal(line.factor, 4, true) };
    return isOrder ? { ...common, ordered_qty: normalizePurPayloadDecimal(line.quantity, 2, true),
      unit_cost: normalizePurPayloadDecimal(line.unitCost), discount: normalizePurPayloadDecimal(line.discount || "0"), vat_rate: headerVat }
      : { ...common, requested_qty: normalizePurPayloadDecimal(line.quantity, 2, true) };
  }));
  if (!validDate(input.date) || (input.deliveryDate && !validDate(input.deliveryDate))) return block("MEERP_DRAFT_DATE_ERROR");
  const delivery = input.deliveryDate || null, text = input.text.trim();
  return isOrder ? { action: "CREATE", project, vendor, delivery_storage: storage, order_date: input.date,
    expected_delivery_date: delivery, vat_rate: headerVat, notes: text, items }
    : { action: "CREATE", project, target_storage: storage, request_date: input.date, required_date: delivery, purpose: text, items };
};

export const buildPurReceiptPayloadData = (input: PurReceiptInput): Record<string, unknown> => {
  const issue = receiptPayloadOrderIssue(input.po);
  if (issue) return block(issue);
  if (!readId(input.storageId) || !validDate(input.date) || !input.items.length || input.items.length > 100
    || input.metadata.deliveryNumber.length > 255) return block();
  if (input.metadata.totalIncludingVat.trim()) receiptDecimal(input.metadata.totalIncludingVat);
  const totals = new Map<number, number>(), batches = new Map<string, string>();
  let receivedTotal = 0;
  const items = input.items.map((line, index) => withPayloadLine(index + 1, () => {
    const source = input.po.items.find((item): item is PurOrderLine => item.kind === "purchaseOrders" && item.id === line.poItemId);
    if (!source || line.productId !== source.productId || line.unitId !== source.unitId
      || receiptDecimal(line.factor, 4, true) !== receiptDecimal(source.conversionFactor, 4, true)) return block("MEERP_RECEIPT_SOURCE_ERROR");
    if (typeof line.hasLots !== "boolean" || line.hasLots !== source.hasLots) return block("MEERP_RECEIPT_PRODUCT_ERROR");
    const delivered = receiptUnits(line.delivered), accepted = receiptUnits(line.accepted), rejected = receiptUnits(line.rejected), factor = receiptUnits(line.factor, 4);
    if (delivered <= 0 || delivered !== accepted + rejected) return block();
    for (const quantity of [accepted, rejected]) {
      const base = quantity * factor;
      if (!Number.isSafeInteger(base) || base % 1000000 !== 0) return block("MEERP_DRAFT_STOCK_INTEGER_ERROR");
    }
    if (rejected > 0 && (!readId(input.rejectedStorageId) || input.rejectedStorageId === input.storageId || !line.reason.trim())) return block("MEERP_DRAFT_REJECT_ERROR");
    if (line.reason.length > 255 || line.batch.length > 100) return block();
    if (line.hasLots) {
      if (!line.batch.trim() || !validDate(line.manufactured) || !validDate(line.expires) || line.manufactured > line.expires
        || line.manufactured > input.date || (accepted > 0 && line.expires <= input.date)) return block("MEERP_DRAFT_LOT_ERROR");
      const batchKey = `${line.productId}:${line.batch.trim()}`, dates = `${line.manufactured}:${line.expires}`;
      if (batches.has(batchKey) && batches.get(batchKey) !== dates) return block("MEERP_DRAFT_LOT_CONFLICT_ERROR");
      batches.set(batchKey, dates);
    } else if (line.manufactured || line.expires) return block("MEERP_DRAFT_NON_LOT_DATES_ERROR");
    receiptPayloadBaseCostCents(line.unitCost, line.factor);
    totals.set(line.poItemId, (totals.get(line.poItemId) || 0) + accepted); receivedTotal += accepted + rejected;
    return { po_item: line.poItemId, received_qty: receiptDecimal(line.delivered), accepted_qty: receiptDecimal(line.accepted), rejected_qty: receiptDecimal(line.rejected),
      rejection_reason: line.reason.trim(), unit: line.unitId, conversion_factor: receiptDecimal(line.factor, 4, true),
      unit_cost: receiptDecimal(line.unitCost), batch_no: line.batch.trim(), mfg_date: line.hasLots ? line.manufactured : null, exp_date: line.hasLots ? line.expires : null };
  }));
  if (receivedTotal <= 0) return block("MEERP_RECEIPT_DATA_ERROR");
  totals.forEach((total, id) => {
    const source = input.po.items.find((line): line is PurOrderLine => line.kind === "purchaseOrders" && line.id === id)!;
    if (!Number.isSafeInteger(total) || total > receiptUnits(source.orderedQuantity) - receiptUnits(source.receivedQuantity)) return block("MEERP_DRAFT_REMAINING_ERROR");
  });
  return { po: input.po.id, storage: input.storageId, rejected_storage: input.rejectedStorageId, receipt_date: input.date, items };
};
